import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AuthContext } from '../../Auth/context/AuthContext';
import PDFViewer from '../components/PDFViewer';

const TakeoffsPage = () => {
  const { id: bidId } = useParams();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const authToken = auth.token;

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);

  // Fetch list of takeoff drawings
  useEffect(() => {
    const fetchTakeoffs = async () => {
      setLoading(true);
      try {
        const resp = await axios.get(
          `/api/myteconstruction/bids/${bidId}/takeoffs`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        setFiles(resp.data);
      } catch (err) {
        console.error(err);
        const msg = err.response?.data?.error || 'Failed to load drawings';
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchTakeoffs();
  }, [bidId, authToken]);

  // Handle file selection & upload
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    try {
      // Sign upload
      const signResp = await axios.post(
        `/api/myteconstruction/bids/${bidId}/takeoffs/sign`,
        {
          filename: file.name,
          contentType: file.type,
          size: file.size
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { uploadUrl, key } = signResp.data;
      // Upload to S3
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type }
      });
      // Save metadata
      const saveResp = await axios.post(
        `/api/myteconstruction/bids/${bidId}/takeoffs`,
        {
          key,
          filename: file.name,
          size: file.size,
          contentType: file.type
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setFiles((prev) => [saveResp.data, ...prev]);
      toast.success('Drawing uploaded');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    }
  };

  // Handle viewing a PDF
  const handleView = async (file) => {
    try {
      const resp = await axios.get(
        `/api/myteconstruction/bids/${bidId}/takeoffs/${file._id}/download`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setViewerUrl(resp.data.url);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load PDF');
    }
  };

  return (
    <div className="p-4">
      <button
        onClick={() => navigate(-1)}
        className="text-blue-600 hover:underline mb-4"
      >
        &larr; Back
      </button>
      <h1 className="text-2xl font-bold mb-4">Takeoff Drawings</h1>

      <div className="mb-4">
        <input
          type="file"
          accept="application/pdf"
          onChange={handleUpload}
          className="border p-2 rounded"
        />
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : files.length === 0 ? (
        <div>No drawings uploaded yet.</div>
      ) : (
        <ul className="space-y-2">
          {files.map((file) => (
            <li
              key={file._id}
              className="flex justify-between items-center p-2 border rounded"
            >
              <div>
                <p className="font-medium">{file.fileName}</p>
                <p className="text-sm text-gray-500">
                  {(file.fileSize / 1024).toFixed(1)} KB —{' '}
                  {new Date(file.uploadDate).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => handleView(file)}
                className="text-blue-600 hover:underline"
              >
                View
              </button>
            </li>
          ))}
        </ul>
      )}

      {viewerUrl && (
        <PDFViewer url={viewerUrl} onClose={() => setViewerUrl(null)} />
      )}
    </div>
  );
};

export default TakeoffsPage;